import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import { Box, useMediaQuery } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getActualUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';
import {
  MenuSwitch,
  Menu,
  HeaderLogo,
  AppBannerSpacer,
  Icon,
} from '@pwc/pwc-deals-ui';
import ProjectListSideNavButtons from './ProjectListSideNavButtons';
import ProjectSideNavButtons from './ProjectSideNavButtons';
import {
  toggleMobileSidebarOpened,
  getMobileSidebarOpened,
  showProjectListMenu,
} from '../../../features/appSettings/appSettingsSlice';
import { getProject } from '../../../features/project/projectSlice';
import styles from './SideNav.module.scss';
import { GlobalToggle } from '../GlobalToggle';
import { isUserInsideProjectPath } from '../../helpers/projectInitUtil';

const getWindowHeight = () => window.innerHeight;

const getIsMobileWidth = () => window.innerWidth < 768;

const getDocumentHeight = () => {
  const { body } = document;
  const html = document.documentElement;

  const height = Math.max(
    body.scrollHeight,
    body.offsetHeight,
    html.clientHeight,
    html.scrollHeight,
    html.offsetHeight
  );

  return height;
};

const SideNav = () => {
  const { pathname } = useLocation();
  const actualUserRole = useSelector(getActualUserRole);

  const [isMobileView, setIsMobileView] = useState(false);
  const [showScrollTopButton, setShowScrollTopButton] = useState(false);
  const [showScrollBottomButton, setShowScrollBottomButton] = useState(false);
  const dispatch = useDispatch();
  const open = useSelector(getMobileSidebarOpened);
  const menuId = 'navigation';
  const project = useSelector(getProject);
  const projectId = project && project.id;
  const hasCurrentProject = !!projectId;
  const ref = useRef();
  const isMobile = useMediaQuery('(max-width:767px)');

  const isUserInsideProject = isUserInsideProjectPath();
  // eslint-disable-next-line
  const [topPosition, setTopPosition] = useState(0);

  const showHideScrollButtons = useCallback(
    (menuTopPosition) => {
      if (!getIsMobileWidth() && ref.current) {
        const sidebarRect = ref.current.getBoundingClientRect();
        const windowHeight = getWindowHeight();
        const contentHeight = getDocumentHeight();
        const menuHeight = ref.current.clientHeight - 2; // -2px as buffer for incorrect menu height

        if (contentHeight <= menuHeight) {
          const menuScrollTop =
            menuTopPosition === undefined ? -sidebarRect.top : menuTopPosition;
          if (windowHeight + menuScrollTop < menuHeight) {
            setShowScrollBottomButton(true);
          } else {
            setShowScrollBottomButton(false);
          }
          if (menuScrollTop) {
            setShowScrollTopButton(true);
          } else {
            setShowScrollTopButton(false);
          }
        } else {
          setShowScrollTopButton(false);
          setShowScrollBottomButton(false);
        }
      }
    },
    [setShowScrollTopButton, setShowScrollBottomButton]
  );

  const getBannerHeight = () => {
    const appBannerHeightString =
      document.documentElement.style.getPropertyValue(
        '--app-banner-height-offset'
      );
    return !Number.isNaN(parseInt(appBannerHeightString, 10))
      ? parseInt(appBannerHeightString, 10)
      : 0;
  };

  useEffect(() => {
    // eslint-disable-next-line
    const onScrollEvent = (event) => {
      if (ref.current) {
        const sidebarRect = ref.current.getBoundingClientRect();
        const windowHeight = window.innerHeight + getBannerHeight();
        const scrollTopPosition =
          document.documentElement.scrollTop || document.body.scrollTop;

        if (scrollTopPosition + windowHeight < sidebarRect.height) {
          setTopPosition(scrollTopPosition);
        } else {
          setTopPosition(sidebarRect.height - windowHeight);
        }
      }
    };
    // to turn of transition of the navigation while changing the layout to mobile view, else while changing the layout from desktop to mobile (going from 768px to 767px) the mobile navigation will pop in and animate out
    const onResizeEvent = (e) => {
      setIsMobileView(getIsMobileWidth());
      onScrollEvent(e);
      showHideScrollButtons();
    };
    onResizeEvent();

    let observer;
    if (ResizeObserver) {
      observer = new ResizeObserver(() => showHideScrollButtons());
      observer.observe(document.documentElement);
    }

    window.addEventListener('scroll', onScrollEvent);
    window.addEventListener('resize', onResizeEvent);
    return () => {
      window.removeEventListener('scroll', onScrollEvent);
      window.removeEventListener('resize', onResizeEvent);
      if (observer) observer.unobserve(document.documentElement);
    };
  }, [showHideScrollButtons]);

  const scrollButtonHeight = 40;
  const scrollBy = getWindowHeight() - scrollButtonHeight;

  const scrollDown = () => {
    const menuHeight = ref.current.clientHeight;
    const viewAbleTopPosition = menuHeight - getWindowHeight();
    const newTopPosition =
      topPosition + scrollBy > viewAbleTopPosition
        ? viewAbleTopPosition
        : topPosition + scrollBy;
    setTopPosition(newTopPosition);
    showHideScrollButtons(newTopPosition);
  };

  const scrollUp = () => {
    const newTopPosition = topPosition < scrollBy ? 0 : topPosition - scrollBy;
    setTopPosition(newTopPosition);
    showHideScrollButtons(newTopPosition);
  };

  const smoothScrollClass =
    showScrollBottomButton || showScrollTopButton
      ? styles.navBarSmoothScroll
      : '';

  const closeSideNav = () => {
    dispatch(toggleMobileSidebarOpened(false));
  };
  const showProjectList = useSelector(showProjectListMenu);

  const showGlobalToggle = useMemo(() => {
    if (pathname === '/projects' || !isMobile || !open || showProjectList) {
      return false;
    }
    return actualUserRole === USER_ROLES.PWC_USER;
  }, [actualUserRole, pathname, isMobile, open, showProjectList]);

  return (
    <div
      className={`side-bar ${open} ${smoothScrollClass} ${styles.navMenu}`}
      style={{ top: isMobileView ? 0 : -topPosition }}
      ref={ref}
    >
      <AppBannerSpacer />
      <Box className="side-bar-logo">
        <MenuSwitch
          open={open}
          onClick={() => dispatch(toggleMobileSidebarOpened())}
          menuId={menuId}
        />
        <HeaderLogo width={42} dataTestId="header-logo" />
        {showGlobalToggle ? (
          <Box sx={{ position: 'absolute', top: '1rem', right: '0rem' }}>
            <GlobalToggle />
          </Box>
        ) : null}
      </Box>
      {showScrollTopButton && (
        <button
          type="button"
          className={styles.scrollUpButton}
          onClick={scrollUp}
        >
          <Icon name="arrow-up" width="20" />
        </button>
      )}
      {showScrollBottomButton && (
        <button
          type="button"
          className={styles.scrollDownButton}
          onClick={scrollDown}
        >
          <Icon name="arrow-down" width="20" />
        </button>
      )}
      <Menu
        data-testid="side-bar"
        open={open}
        style={isMobileView ? {} : { transition: 'none' }}
      >
        {hasCurrentProject
          ? !project.isLoading && (
              <ProjectSideNavButtons closeSideNav={closeSideNav} />
            )
          : !isUserInsideProject && (
              <ProjectListSideNavButtons
                closeSideNav={closeSideNav}
                mobile={open}
              />
            )}
        <AppBannerSpacer />
      </Menu>
    </div>
  );
};

export default SideNav;
